import { Button, openModal } from '@ff-it/ui';
import { ConfirmDialog } from 'components/Actions/createConfirmDialog';
import { ReactElement, ReactNode, useState } from 'react';
import { toast } from 'react-toastify';
import { fetcher } from 'services';
import type { KeyedMutator } from 'swr';
import { formatError } from './error';
import { confirmDialogStyle } from './style.css';
import type { Integration } from './types';

type ActionButtonProps = {
  url: string;
  method?: 'POST' | 'DELETE';
  label: string;
  icon: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  confirm?: { title: string; body?: ReactNode };
  successMessage?: string;
  mutate: KeyedMutator<Integration>;
};

export function ActionButton({
  url,
  method = 'POST',
  label,
  icon,
  variant = 'secondary',
  confirm,
  successMessage,
  mutate,
}: ActionButtonProps): ReactElement {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant={variant}
      icon={icon}
      size="sm"
      disabled={busy}
      onClick={async () => {
        if (confirm) {
          const ok = await openModal<boolean>(
            (props) => (
              <ConfirmDialog {...props} title={confirm.title} confirm={label}>
                {confirm.body}
              </ConfirmDialog>
            ),
            {
              testId: `confirm-${label.toLowerCase().replace(/\s+/g, '-')}`,
              className: confirmDialogStyle,
            },
          );
          if (!ok) return;
        }
        setBusy(true);
        try {
          const next = await fetcher<Integration>({ method, url });
          mutate(next, { revalidate: false });
          toast.success(successMessage ?? label);
        } catch (e) {
          const detail = formatError(e);
          toast.error(detail ? `${label} failed: ${detail}` : `${label} failed.`);
          mutate();
        } finally {
          setBusy(false);
        }
      }}
    >
      {label}
    </Button>
  );
}
